import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { MeResponse } from '@/api/generated/types.gen';

export type { MeResponse };

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: ({ signal }): Promise<MeResponse> =>
    api<MeResponse>('/api/v1/me', { signal }),
  retry: (failureCount, error) => {
    if (error instanceof ApiError && error.status === 401) return false;
    return failureCount < 1;
  },
  staleTime: 60_000,
  // Identity data must survive observer-less stretches: the Sidebar reads the
  // router loader, so on pages with no usePermissions subscriber the default
  // 5-min gc would empty this cache and the ⌘K palette would flash
  // "Dashboard-only" until a refetch. beforeLoad re-validates on every
  // navigation anyway.
  gcTime: Infinity,
});

// usePermissions — the current user's effective canonical actions (via roles
// and direct grants), plus a can() helper for gating UI. Reads from the same
// meQuery cache the rest of the app uses (populated by the `_app` route
// loader), so this never triggers its own fetch.
export function usePermissions(): { permissions: string[]; can: (permission: string) => boolean } {
  const { data } = useQuery(meQuery);
  const permissions = data?.permissions ?? [];
  return { permissions, can: (permission: string) => permissions.includes(permission) };
}
